import {useEffect, useMemo, useCallback} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useUpdateProjectDetails} from '../../AdminPanel/api/projectDetailsUpdate'
import {usePointOfInterest} from './pointOfInterest'
import {getBenefitsList, getWorkforcesList} from '../../shared/helpers/common'
import {useAdminPermission} from './adminPermission'
import {
  setClientName,
  setHasUsers,
  setJurisdiction,
  setIsCompleted,
  setProjectNamesList,
  setNavigationProps,
  setCreateProjectNavProps,
  setTrendAnalysisFlag,
  setPersonaFlag,
  setChatbotFlag,
  setExportFlag,
  setChatbotExportFlag,
  setChatbotCopyFlag,
  selectClientName,
  selectHasUsers,
  selectJurisdiction,
  selectIsCompleted,
  selectProjectNamesList,
  selectNavigationProps,
  selectCreateProjectNavProps,
  selectTrendAnalysisFlag,
  selectPersonaFlag,
  selectChatbotFlag,
  selectExportFlag,
  selectChatbotExportFlag,
  selectChatbotCopyFlag
} from '../slices/projectDetailsSlice'
import {selectAdminPermissionLoading} from '../slices/adminPermissionSlice'
import {useConfig} from './config'

export const useProjectDetails = () => {
  const dispatch = useDispatch()

  const {useCasesConfig} = useConfig() || {}
  // Redux state selectors
  const clientName = useSelector(selectClientName)
  const hasUsers = useSelector(selectHasUsers)
  const jurisdiction = useSelector(selectJurisdiction)
  const isCompleted = useSelector(selectIsCompleted)
  const projectNamesList = useSelector(selectProjectNamesList)
  const navigationProps = useSelector(selectNavigationProps)
  const createProjectNavProps = useSelector(selectCreateProjectNavProps)
  const trendAnalysisFlag = useSelector(selectTrendAnalysisFlag)
  const personaFlag = useSelector(selectPersonaFlag)
  const chatbotFlag = useSelector(selectChatbotFlag)
  const exportFlag = useSelector(selectExportFlag)
  const chatbotExportFlag = useSelector(selectChatbotExportFlag)
  const chatbotCopyFlag = useSelector(selectChatbotCopyFlag)

  const filterDetailsLoading = useSelector(selectAdminPermissionLoading)
  // Get data from other hooks
  const {pointOfInterests} = usePointOfInterest()
  const {states, filters, filterDetailsError, refetchFilterDetailsList} = useAdminPermission()

  const pointOfInterestsDataRaw = useMemo(
    () =>
      pointOfInterests?.map((poiValue) => ({
        title: poiValue.label,
        POI_ID: poiValue.poi_id,
        IS_ACTIVE: poiValue.enabled
      })) || [],
    [pointOfInterests]
  )

  // Stabilize reference — only return a new array when actual POI IDs change
  const pointOfInterestsDataKey = pointOfInterestsDataRaw
    .map((p) => p.POI_ID)
    .sort()
    .join(',')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pointOfInterestsData = useMemo(() => pointOfInterestsDataRaw, [pointOfInterestsDataKey])

  const benefits = getBenefitsList()
  const workforces = getWorkforcesList()
  const useCases = useCasesConfig.data

  const [updateGroup, {data: updateProjectList, loading: loadingUpdateProject, error}] = useUpdateProjectDetails()

  // Effect to update jurisdiction from states
  // Guard dispatch to avoid new array references when data hasn't changed
  useEffect(() => {
    if (states?.stateList) {
      const jurisdictionData = states.stateList.map(({STATE_NAME: title, ...rest}) => ({title, ...rest}))
      const newKeys = jurisdictionData
        .map((j) => j.STATE_ID)
        .sort()
        .join(',')
      const currentKeys = jurisdiction
        .map((j) => j.STATE_ID)
        .sort()
        .join(',')
      if (newKeys !== currentKeys) {
        dispatch(setJurisdiction(jurisdictionData))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [states, dispatch])

  // Action dispatchers
  const setClientNameAction = useCallback((value) => dispatch(setClientName(value)), [dispatch])
  const setHasUsersAction = useCallback((value) => dispatch(setHasUsers(value)), [dispatch])
  const setIsCompletedAction = useCallback((value) => dispatch(setIsCompleted(value)), [dispatch])
  const setProjectNamesListAction = useCallback((value) => dispatch(setProjectNamesList(value)), [dispatch])
  const setNavigationPropsAction = useCallback((value) => dispatch(setNavigationProps(value)), [dispatch])
  const setCreateProjectNavPropsAction = useCallback((value) => dispatch(setCreateProjectNavProps(value)), [dispatch])
  const setTrendAnalysisFlagAction = useCallback((value) => dispatch(setTrendAnalysisFlag(value)), [dispatch])
  const setPersonaFlagAction = useCallback((value) => dispatch(setPersonaFlag(value)), [dispatch])
  const setChatbotFlagAction = useCallback((value) => dispatch(setChatbotFlag(value)), [dispatch])
  const setExportFlagAction = useCallback((value) => dispatch(setExportFlag(value)), [dispatch])
  const setChatbotExportFlagAction = useCallback((value) => dispatch(setChatbotExportFlag(value)), [dispatch])
  const setChatbotCopyFlagAction = useCallback((value) => dispatch(setChatbotCopyFlag(value)), [dispatch])

  return {
    states,
    filterDetailsLoading,
    refetchFilterDetailsList,
    filterDetailsError,
    filters, // All filters from adminPermission hook
    isCompleted,
    setIsCompleted: setIsCompletedAction,
    projectNamesList,
    setProjectNamesList: setProjectNamesListAction,
    jurisdiction,
    updateGroup,
    updateProjectList,
    loadingUpdateProject,
    error,
    clientName,
    setClientName: setClientNameAction,
    hasUsers,
    setHasUsers: setHasUsersAction,
    navigationProps,
    setNavigationProps: setNavigationPropsAction,
    createProjectNavProps,
    setCreateProjectNavProps: setCreateProjectNavPropsAction,
    personaFlag,
    setPersonaFlag: setPersonaFlagAction,
    trendAnalysisFlag,
    setTrendAnalysisFlag: setTrendAnalysisFlagAction,
    chatbotFlag,
    setChatbotFlag: setChatbotFlagAction,
    chatbotExportFlag,
    setChatbotExportFlag: setChatbotExportFlagAction,
    chatbotCopyFlag,
    setChatbotCopyFlag: setChatbotCopyFlagAction,
    exportFlag,
    setExportFlag: setExportFlagAction,
    benefits,
    workforces,
    useCases,
    pointOfInterestsData,
    loadingSdoh: filterDetailsLoading, // Alias for backward compatibility
    errorLoadingsdoh: filterDetailsError, // Alias for backward compatibility
    refetchSdoh: refetchFilterDetailsList // Alias for backward compatibility
  }
}
